import axios, { AxiosInstance } from 'axios';

// Создаем экземпляр axios с базовой конфигурацией
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://api.denisphones.org',
  timeout: 30000,
  withCredentials: true
});

api.interceptors.request.use(
  (config) => config,
  (error) => {
    console.error('❌ Request error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('❌ Response error:', {
      status: error.response?.status,
      url: error.config?.url,
      message: error.message,
      data: error.response?.data
    });
    return Promise.reject(error);
  }
);

// Интерфейсы для типизации
export interface User {
  _id: string;
  userName: string;
  chatId: string;
  group: string;
  time?: string;
  send: boolean;
  graduated: boolean;
  isNewUser: boolean;
  isTeacher: boolean;
  firstName?: string;
  lastName?: string;
  Blocked: boolean;
  deactivated?: boolean;
  chatNotFound?: boolean;
  fcmToken?: string;
  appToken?: string;
  lastScheduleSentDate?: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  lastUpdated?: string | Date;
}

export interface BotState {
  enabled: boolean;
  autoSendEnabled: boolean;
  scheduleSendingEnabled: boolean;
}

export interface UserCounts {
  totalUsers: number;
  activeUsers: number;
}

export interface BroadcastParams {
  html: string;
  onlyActive: boolean;
  allUsers: boolean;
  whitelistMode: boolean;
  includeNewUsers: boolean;
  includeTeachers: boolean;
  includeHasApp: boolean;
  group?: string;
  scheduleTime?: string;
}

export interface BroadcastResponse {
  success: boolean;
  total?: number;
  sent?: number;
  failed?: number;
  errors?: any[];
  broadcastId?: string;
  /** Ссылки на отправленные в чаты сообщения (для удаления при удалении новости) */
  sentRefs?: { chatId: number; messageId: number }[];
  scheduled?: boolean;
  scheduledFor?: string;
  scheduledId?: string;
  error?: string;
}

export interface BroadcastStatus {
  id: string;
  startedAt: number;
  onlyActive: boolean;
  cancelled: boolean;
  inProgress: boolean;
  total: number;
  sent: number;
  failed: number;
}

export interface ScheduledBroadcast {
  id: string;
  html: string;
  imageUrls?: string[];
  onlyActive: boolean;
  allUsers: boolean;
  whitelistMode: boolean;
  includeNewUsers: boolean;
  includeTeachers: boolean;
  includeHasApp: boolean;
  group: string;
  /** Куда будет отправлена запланированная новость: в бот */
  sendToBot?: boolean;
  /** Куда будет отправлена запланированная новость: в приложение */
  sendToApp?: boolean;
  scheduleTime: string;
  createdAt: string;
  status: 'scheduled' | 'sent' | 'cancelled';
  sentAt?: string;
}

export interface LogEntry {
  _id: string;
  message: string;
  timestamp: number;
  command: string;
  logtype: string;
  details?: any;
}

export interface LogsResponse {
  success: boolean;
  logs: LogEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface LogDetails {
  _id: string;
  command: string;
  logtype: string;
  timestamp: number;
  userName?: string;
  chatId?: number;
  chatType?: string;
  group?: string;
  extraData?: any;
  errMsg?: string;
}

export interface ChatMessage {
  _id: string;
  chatId: number;
  messageId: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp: string;
  parseMode: string;
  userName: string;
  group: string;
  isRead?: boolean;
}

export interface Chat {
  chatId: number;
  userName: string;
  group: string;
  lastMessage: ChatMessage;
  messageCount: number;
  unreadCount?: number;
}

export interface ChatsResponse {
  success: boolean;
  chats: Chat[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface MessagesResponse {
  success: boolean;
  messages: ChatMessage[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface SendMessageParams {
  message: string;
  parseMode?: 'HTML' | 'Markdown';
}

export interface SendMessageResponse {
  success: boolean;
  message: ChatMessage;
  telegramMessageId: number;
}


export interface UserPhotos {
  success: boolean;
  photos: Array<{
    url: string;
    width: number;
    height: number;
    fileId: string;
  }>;
  count: number;
  message?: string;
}

// Глобальный обработчик ошибок
let globalErrorHandler: ((error: string) => void) | null = null;

// Функция для установки глобального обработчика ошибок
export const setGlobalErrorHandler = (handler: (error: string) => void) => {
  globalErrorHandler = handler;
};

// Функция для обработки ошибок API
const handleApiError = (error: any, operation: string) => {
  console.error(`Ошибка при ${operation}:`, error);
  
  let errorMessage = '';
  
  if (error.response) {
    // Сервер ответил с кодом ошибки
    const { status, data } = error.response;
    if (status === 401) {
      // Неавторизован - перенаправляем на страницу входа
      window.location.href = '/login';
      return;
    }
    errorMessage = data.error || `Ошибка сервера: ${status}`;
  } else if (error.request) {
    // Запрос был отправлен, но ответа не получено
    errorMessage = 'Сервер недоступен. Проверьте подключение к интернету.';
  } else {
    // Что-то пошло не так при настройке запроса
    errorMessage = `Ошибка запроса: ${error.message}`;
  }
  
  // Вызываем глобальный обработчик ошибок, если он установлен
  if (globalErrorHandler) {
    globalErrorHandler(errorMessage);
  }
  
  throw new Error(errorMessage);
};

// Обертка для API вызовов с обработкой ошибок
const apiCall = async <T>(apiFunction: () => Promise<T>, operation: string): Promise<T> => {
  try {
    return await apiFunction();
  } catch (error) {
    handleApiError(error, operation);
    throw error;
  }
};

// API для аутентификации
export const authAPI = {
  login: (password: string): Promise<{success: boolean}> =>
    apiCall(() => api.post('/site/login', null, { params: { password } }).then(res => res.data), 'вход в систему'),
  
  logout: (): Promise<{success: boolean}> =>
    apiCall(() => api.post('/site/logout').then(res => res.data), 'выход из системы'),
  
  checkAuth: (): Promise<{success: boolean}> =>
    apiCall(() => api.get('/site/check-auth').then(res => res.data), 'проверка авторизации'),
};

// API для управления ботом
export const botAPI = {
  getState: (): Promise<BotState> =>
    apiCall(() => api.get('/site/bot-state').then(res => res.data), 'получение состояния бота'),
  
  setState: (state: Partial<BotState>): Promise<BotState> =>
    apiCall(() => api.post('/site/bot-state', state).then(res => res.data), 'обновление состояния бота'),
};

// API для состояния бота (альтернативное название для совместимости)
export const botStateAPI = {
  get: (): Promise<BotState> =>
    apiCall(() => api.get('/site/bot-state').then(res => res.data), 'получение состояния бота'),
  
  update: (state: Partial<BotState>): Promise<{success: boolean, state: BotState}> =>
    apiCall(() => api.post('/site/bot-state', state).then(res => res.data), 'обновление состояния бота'),
};

// API для пользователей
export const usersAPI = {
  getCounts: (): Promise<UserCounts> =>
    apiCall(() => api.get('/site/user-counts').then(res => res.data), 'получение статистики пользователей'),
  
  getUsers: (page: number = 1, limit: number = 50, filters?: UserFilters): Promise<{success: boolean, users: User[], pagination: any}> =>
    apiCall(() => api.get('/site/users', { params: { page, limit, ...filters } }).then(res => res.data), 'получение списка пользователей'),
};

export interface UserFilters {
  groups?: string[];
  active?: boolean;
  blocked?: boolean;
  graduated?: boolean;
  teachers?: boolean;
  newUsers?: boolean;
  hasApp?: boolean;
  hasAppToken?: boolean;
  search?: string;
}

// API для рассылки
export const broadcastAPI = {
  getRecipientsCount: (params: Partial<BroadcastParams>): Promise<{success: boolean, count: number}> =>
    apiCall(() => api.get('/site/broadcast-count', { params }).then(res => res.data), 'подсчет получателей рассылки'),
  
  getCount: (params: Partial<BroadcastParams>): Promise<{success: boolean, count: number}> =>
    apiCall(() => api.get('/site/broadcast-count', { params }).then(res => res.data), 'подсчет получателей рассылки'),
  
  send: (params: BroadcastParams): Promise<BroadcastResponse> =>
    apiCall(() => api.post('/site/broadcast', params).then(res => res.data), 'отправка рассылки'),
  
  getStatus: (): Promise<BroadcastStatus> =>
    apiCall(() => api.get('/site/broadcast/status').then(res => res.data), 'получение статуса рассылки'),
  
  cancel: (deleteSent: boolean = false): Promise<{success: boolean, cancelled: boolean, deleted: number}> =>
    apiCall(() => api.post('/site/broadcast/cancel', { deleteSent }).then(res => res.data), 'отмена рассылки'),
  
  getScheduled: (): Promise<{success: boolean, scheduled: ScheduledBroadcast[]}> =>
    apiCall(() => api.get('/site/scheduled-broadcasts').then(res => res.data), 'получение запланированных рассылок'),
  
  deleteScheduled: (id: string): Promise<{success: boolean, error?: string}> =>
    apiCall(() => api.delete(`/site/scheduled-broadcasts/${id}`).then(res => res.data), 'удаление запланированной рассылки'),
};

// API для групп
export const groupsAPI = {
  getGroups: (): Promise<{success: boolean, groups: string[]}> =>
    apiCall(() => api.get('/site/groups').then(res => ({ success: true, groups: res.data })), 'получение списка групп'),
};

export interface NewsListItem {
  id: string;
  messageId?: string;
  content: string;
  imageUrl: string | null;
  imageUrls: string[] | null;
  sender: string;
  timestamp: number;
  /** Была ли новость отправлена в бот Telegram */
  sentToBot?: boolean;
  /** Была ли новость отправлена в приложение */
  sentToApp?: boolean;
}

export interface NewsListResponse {
  news: NewsListItem[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

/** Ссылка на сообщение в чате пользователя (для удаления при удалении новости) */
export interface BotSentRef {
  chatId: number;
  messageId: number;
}

// Параметри для планування новини (бот + додаток)
export interface ScheduleNewsParams {
  message: string;
  imageUrls?: string[];
  sendToBot: boolean;
  sendToApp: boolean;
  scheduleTime: string;
  onlyActive?: boolean;
  allUsers?: boolean;
  whitelistMode?: boolean;
  includeNewUsers?: boolean;
  includeTeachers?: boolean;
  includeHasApp?: boolean;
  group?: string;
}

export interface ScheduleNewsResponse {
  success: boolean;
  scheduled?: boolean;
  scheduledFor?: string;
  scheduledId?: string;
  error?: string;
}

// API для новостей
export const newsAPI = {
  sendNews: (params: { message: string; imageUrl?: string | null; imageUrls?: string[]; sentToBot?: boolean; botSentRefs?: BotSentRef[] }): Promise<{ success: boolean; messageId?: string; sentCount?: number; errorCount?: number }> =>
    apiCall(() => api.post('/site/app/send-news', params).then(res => res.data), 'отправка новости'),
  /** Запланувати відправку новини (в бот і/або в додаток) на заданий час */
  scheduleNews: (params: ScheduleNewsParams): Promise<ScheduleNewsResponse> =>
    apiCall(() => api.post('/site/schedule-news', params).then(res => res.data), 'планування новини'),
  /** Лог новости «только в бот» для отображения в списке отправленных */
  logNews: (params: { message: string; imageUrl?: string | null; imageUrls?: string[]; botSentRefs?: BotSentRef[] }): Promise<{ success: boolean; id?: string }> =>
    apiCall(() => api.post('/site/news/log', params).then(res => res.data), 'логирование новости (бот)'),
  uploadPhoto: (file: File): Promise<{ success: boolean; url: string }> =>
    apiCall(async () => {
      const formData = new FormData();
      formData.append('photo', file);
      const res = await api.post('/site/upload/news-photo', formData);
      return res.data;
    }, 'загрузка фото для новости'),
  getNewsList: (page: number = 0, limit: number = 20): Promise<NewsListResponse> =>
    apiCall(() => api.get('/site/news', { params: { page, limit } }).then(res => res.data), 'получение списка новостей'),
  deleteNews: (id: string): Promise<{ success: boolean; deletedId: string }> =>
    apiCall(() => api.delete(`/site/news/${id}`).then(res => res.data), 'удаление новости'),
  /** Тестовая отправка черновика новости пользователю 1297952533 */
  sendTest: (params: { message: string; imageUrls?: string[] }): Promise<{ success: boolean; chatId?: number; error?: string }> =>
    apiCall(() => api.post('/site/news/send-test', params).then(res => res.data), 'тестовая отправка новости'),
};

// API для логов
export const logsAPI = {
  getLogs: (page: number = 1, limit: number = 50): Promise<LogsResponse> =>
    apiCall(() => api.get('/site/logs', { params: { page, limit } }).then(res => res.data), 'получение логов'),
  
  getLogDetails: (id: string): Promise<LogDetails> =>
    apiCall(() => api.get(`/site/log-details/${id}`).then(res => res.data), 'получение деталей лога'),
};

// API для фотографий пользователей
export const photosAPI = {
  getUserPhotos: (userId: number | string): Promise<UserPhotos> =>
    apiCall(() => api.get(`/site/user-photos/${userId}`).then(res => res.data), 'получение фотографий пользователя'),
};

// API для чатов
export const chatsAPI = {
  getChats: (page: number = 1, limit: number = 20, search?: string): Promise<ChatsResponse> =>
    apiCall(() => api.get('/site/chats', { params: { page, limit, search } }).then(res => res.data), 'получение списка чатов'),
  
  getMessages: (chatId: number, page: number = 1, limit: number = 50): Promise<MessagesResponse> =>
    apiCall(() => api.get(`/site/chat/${chatId}/messages`, { params: { page, limit } }).then(res => res.data), 'получение сообщений чата'),
  
  getChatMessages: (chatId: number, page: number = 1, limit: number = 50): Promise<MessagesResponse> =>
    apiCall(() => api.get(`/site/chat/${chatId}/messages`, { params: { page, limit } }).then(res => res.data), 'получение сообщений чата'),
  
  getUnreadCount: (chatId: number, lastReadMessageId: number = 0): Promise<{success: boolean, unreadCount: number}> =>
    apiCall(() => api.get(`/site/chat/${chatId}/unread-count`, { params: { lastReadMessageId } }).then(res => res.data), 'получение количества непрочитанных сообщений'),
  
  sendMessage: (chatId: number, params: SendMessageParams): Promise<SendMessageResponse> =>
    apiCall(() => api.post(`/site/chat/${chatId}/send`, params).then(res => res.data), 'отправка сообщения'),

};

// API для статуса прочтения сообщений
export const readStatusAPI = {
  getReadStatuses: (): Promise<{success: boolean, readStatuses: {[chatId: number]: number}}> =>
    apiCall(() => api.get('/site/chats/read-status').then(res => res.data), 'получение статуса прочтения'),
  
  updateReadStatus: (chatId: number, lastReadMessageId: number): Promise<{success: boolean, chatId: number, lastReadMessageId: number}> =>
    apiCall(() => api.post(`/site/chats/${chatId}/read-status`, { lastReadMessageId }).then(res => res.data), 'обновление статуса прочтения'),
  
  markAllAsRead: (chatReadStatuses: {[chatId: number]: number}): Promise<{success: boolean, message: string}> =>
    apiCall(() => api.post('/site/chats/read-status/mark-all-read', { chatReadStatuses }).then(res => res.data), 'отметка всех как прочитанных'),
};

// API для локальной БД messages_db
export const localDbAPI = {
  getUsers: (page: number = 1, limit: number = 50, search: string = ''): Promise<any> =>
    apiCall(() => api.get('/site/local-db/users', { params: { page, limit, search } }).then(res => res.data), 'получение пользователей из локальной БД'),
  
  getMessages: (page: number = 1, limit: number = 50, search: string = '', chatId?: number): Promise<any> =>
    apiCall(() => api.get('/site/local-db/messages', { params: { page, limit, search, chatId } }).then(res => res.data), 'получение сообщений из локальной БД'),
  
  deleteMessage: (messageId: string): Promise<any> =>
    apiCall(() => api.delete(`/site/local-db/messages/${messageId}`).then(res => res.data), 'удаление сообщения из локальной БД'),
  
  blockUser: (chatId: number, duration: string, reason?: string, customBlockedUntil?: string): Promise<any> =>
    apiCall(() => api.post(`/site/local-db/users/${chatId}/block`, { duration, reason, customBlockedUntil }).then(res => res.data), 'блокировка пользователя'),
  
  unblockUser: (chatId: number): Promise<any> =>
    apiCall(() => api.post(`/site/local-db/users/${chatId}/unblock`).then(res => res.data), 'разблокировка пользователя'),

  /** Восстановить стрик с момента потери (lastMessageDate = вчера, стрик не сбрасывается) */
  restoreStreak: (chatId: number): Promise<{ success: boolean; currentStreak?: number }> =>
    apiCall(() => api.post(`/site/local-db/users/${chatId}/streak/restore`).then(res => res.data), 'восстановление стрика'),

  /** Установить своё число стрика (currentStreak обязателен, maxStreak опционально) */
  setStreak: (chatId: number, currentStreak: number, maxStreak?: number): Promise<{ success: boolean; currentStreak: number; maxStreak: number }> =>
    apiCall(() => api.patch(`/site/local-db/users/${chatId}/streak`, { currentStreak, maxStreak }).then(res => res.data), 'установка стрика'),
  
  sendAppMessage: (chatId: number, message: string): Promise<any> =>
    apiCall(() => api.post('/site/app/send-message', { chatId, message }).then(res => res.data), 'отправка сообщения пользователю через приложение'),

  getPurchaseHistory: (page?: number, limit?: number) =>
    apiCall(() => api.get('/site/local-db/purchase-history', { params: { page: page ?? 1, limit: limit ?? 50 } }).then(res => res.data), 'получение истории покупок'),

  refundPurchase: (purchaseId: string) =>
    apiCall(() => api.post(`/site/local-db/purchase-history/${purchaseId}/refund`).then(res => res.data), 'возврат звёзд за покупку'),
};

export default api;